import { type ChatMessageType } from "@/api/chat";
import { useUser } from "@/api/user";
import { BotIcon, CheckIcon, FileIcon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ChatMessage = ({
  message,
  setPdfViewerDoc,
}: {
  message: ChatMessageType;
  setPdfViewerDoc?: (doc: { id: string; filename: string }) => void;
}) => {
  const user = useUser();
  return (
    <div>
      <div
        className={`relative flex w-fit min-w-[60px] max-w-[80%] flex-col whitespace-pre-wrap rounded-lg px-3 text-sm leading-6 
          ${message.sender !== "event" ? "py-2" : ""} ${
            message.sender === "event"
              ? "mt-[-10px]"
              : message.sender == "human"
                ? "bg-muted"
                : "bg-cf-foreground text-primary-foreground"
          } ${message.sender === "event" ? "mx-auto" : user.data?.id === message.user?.id ? "ml-auto" : "mr-auto"}`}
      >
        <div className="flex flex-row gap-1 text-xs">
          {message.sender === "event" ? (
            <>
              {[
                { events: message.events?.context_added, action: "added" },
                { events: message.events?.context_removed, action: "removed" },
                { events: message.events?.user_added, action: "added" },
                { events: message.events?.user_removed, action: "removed" },
              ].map(({ events, action }) =>
                events?.map((ev, index) => (
                  <span
                    key={`${action}-${index}`}
                    className="text-muted-foreground"
                  >
                    <span className="font-semibold">
                      {message.user?.first_name} {message.user?.last_name}
                    </span>{" "}
                    {action} {ev.type}{" "}
                    <span className="font-semibold">{ev.item?.name}</span>
                  </span>
                )),
              )}
            </>
          ) : message.sender === "ai" ? (
            <>
              <BotIcon className="h-4 w-4 text-white" />{" "}
              <span className="text-white">ForgeIQ</span>
            </>
          ) : (
            <span className="text-muted-foreground">
              {message.user?.first_name
                ? `${message.user?.first_name} ${message.user?.last_name}`
                : message.user?.email}
            </span>
          )}
        </div>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children, ...props }) => {
              const linkClasses = "font-semibold hover:underline";
              if (href)
                return (
                  <Link to={href} className={linkClasses} {...props}>
                    {children}
                  </Link>
                );
            },
          }}
        >
          {message.content}
        </ReactMarkdown>
        {message.tool_results?.length ? (
          <div className="mt-2">
            <Accordion type="single" collapsible>
              <AccordionItem value="tool-results" className="border-none">
                <AccordionTrigger className="border-b py-2 pb-1 text-xs hover:no-underline">
                  Agent actions ({message.tool_results.length})
                </AccordionTrigger>
                <AccordionContent>
                  <div className="mt-2 flex flex-col gap-2">
                    {message.tool_results.map((result, index) => (
                      <div
                        key={`tool-result-${index}`}
                        className="flex items-center gap-x-1 text-xs"
                      >
                        <div>
                          <CheckIcon className="w-4" />
                        </div>{" "}
                        {result}
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        ) : null}
      </div>
      {message.attachments?.length ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {message.attachments.map((attachment, index) => (
            <div
              key={`${attachment.filename}-${index}`}
              className="overflow-hidden rounded-md border border-muted-foreground/30"
            >
              <img
                src={`data:${attachment.mime_type};base64,${attachment.data}`}
                alt={attachment.filename}
                className="h-32 w-32 object-cover"
              />
            </div>
          ))}
        </div>
      ) : null}
      {message.sources?.length ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {message.sources.map((source) => (
            <div
              key={source.id}
              className="flex cursor-pointer gap-1 rounded-full bg-muted/90 p-2 text-xs text-muted-foreground hover:bg-muted/60"
              onClick={() => setPdfViewerDoc?.(source)}
            >
              <FileIcon className="h-4 w-4" /> {source.filename}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
export default ChatMessage;
